import { useCallback } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type {
  CartItem,
  MarketplaceCheckoutAddresses,
  WooCreateOrderPayload,
  WooCustomer,
  WooOrder,
  WooStoreConfig,
} from "../types";
import { formValuesToWooAddress, buildBrazilOrderMetaData } from "../utils/addressHelpers";

function mapCheckoutError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível finalizar a compra.";
}

export interface CheckoutStoreRequest {
  store: WooStoreConfig;
  customer: WooCustomer;
  cartItems: CartItem[];
  userEmail: string;
  addresses: MarketplaceCheckoutAddresses;
}

/**
 * Checkout WooCommerce: billing/shipping separados.
 * Pagamento é escolhido na página da loja parceira (sem payment_method no POST).
 */
export function useWooCommerceCheckout() {
  const checkoutOneTask = useCallback(
    async (request: CheckoutStoreRequest): Promise<WooOrder> => {
      const { store, customer, cartItems, userEmail, addresses } = request;

      if (cartItems.length === 0) {
        throw new Error("Carrinho vazio.");
      }

      const billingSource = { ...addresses.billing };
      const shippingSource = { ...addresses.shipping };
      const shippingCpf = shippingSource.cpf.replace(/\D/g, "");
      if (!billingSource.cpf.replace(/\D/g, "") && shippingCpf) {
        billingSource.cpf = shippingSource.cpf;
      }

      const billing = formValuesToWooAddress(billingSource, userEmail);
      const shipping = formValuesToWooAddress(shippingSource, userEmail);

      const payload: WooCreateOrderPayload = {
        customer_id: customer.id,
        status: "pending",
        billing,
        shipping,
        line_items: cartItems.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
        })),
        set_paid: false,
        meta_data: buildBrazilOrderMetaData(billing, shipping),
      };

      return woocommerceStoreService.createOrder(store, payload);
    },
    [],
  );

  const { run: runCheckoutOne, loading, error } = useAsyncTask({
    task: checkoutOneTask,
    mapError: mapCheckoutError,
  });

  const checkoutStore = useCallback(
    (request: CheckoutStoreRequest) => runCheckoutOne(request),
    [runCheckoutOne],
  );

  return {
    checkoutStore,
    loading,
    error: error ?? null,
  };
}
